export const STRONG_INTERVIEW_MATCH_MINIMUM = 75;

export function formatInterviewJobMeta(value) {
  const cleanValue = String(value || '').trim().toLowerCase();
  if (!cleanValue) return '';

  const labels = {
    on_site: 'On-site',
    onsite: 'On-site',
    on_site_only: 'On-site',
    full_time: 'Full time',
    part_time: 'Part time',
  };

  return labels[cleanValue]
    || cleanValue.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function interviewTurnBudget({
  elapsedSeconds = 0,
  maxDurationSeconds = 20 * 60,
  questionsUsed = 0,
  maxQuestionTurns = 6,
} = {}) {
  const safeElapsed = Math.max(0, Number(elapsedSeconds) || 0);
  const safeDuration = Math.max(1, Number(maxDurationSeconds) || (20 * 60));
  const safeQuestionsUsed = Math.max(0, Number(questionsUsed) || 0);
  const safeMaxQuestionTurns = Math.max(1, Number(maxQuestionTurns) || 6);
  const remainingSeconds = Math.max(0, safeDuration - safeElapsed);
  const questionsRemaining = Math.max(0, safeMaxQuestionTurns - safeQuestionsUsed);
  const mustClose = questionsRemaining === 0 || remainingSeconds <= 90;
  const followUpRule = remainingSeconds <= 5 * 60
    ? 'No follow-up is allowed now. Move directly to the highest-priority uncovered competency, or close if enough evidence exists.'
    : remainingSeconds <= 10 * 60
      ? 'Ask a follow-up only if a critical scoring signal is missing; otherwise move to the next competency.'
      : 'One focused follow-up is allowed only when the answer creates a genuine, job-relevant reason to probe.';

  return {
    elapsedSeconds: safeElapsed,
    remainingSeconds,
    questionsUsed: safeQuestionsUsed,
    questionsRemaining,
    mustClose,
    followUpRule,
  };
}

export function highMatchInterviewOpportunities(feeds) {
  const seen = new Set();
  return Object.values(feeds || {})
    .flatMap((feed) => Object.entries(feed || {}))
    .map(([jobId, value]) => ({ ...(value || {}), jobId: value?.jobId || jobId }))
    .filter((job) => {
      const score = Number(job.fitScore);
      if (
        !job.jobId
        || seen.has(job.jobId)
        || !Number.isFinite(score)
        || score < STRONG_INTERVIEW_MATCH_MINIMUM
      ) return false;
      seen.add(job.jobId);
      return true;
    })
    .sort((left, right) => {
      const scoreDifference = Number(right.fitScore) - Number(left.fitScore);
      if (scoreDifference !== 0) return scoreDifference;
      return Number(left.rank || 999) - Number(right.rank || 999);
    })
    .map((job) => ({
      id: job.jobId,
      title: job.title || 'Untitled role',
      companyName: job.companyName || 'Unknown company',
      fitScore: Math.round(Number(job.fitScore)),
      location: job.location || '',
      workMode: formatInterviewJobMeta(job.remoteType),
      employmentType: formatInterviewJobMeta(job.employmentType),
      reason: job.reason || '',
      description: String(job.description || job.jobDescription || '').trim(),
      responsibilities: job.responsibilities || '',
      requirements: job.requirements || '',
      skills: Array.isArray(job.skills) ? job.skills.filter(Boolean).slice(0, 12) : [],
    }));
}
